import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MainLayout from '../../components/MainLayout';
import { sendAgentMessageStream } from '../../services/api';
import DynamicChart from '../../components/DynamicChart';
import TableRenderer from '../../components/TableRenderer';
import { getStoredUser } from '../../services/authService';
import './AIAnalysis.css';

// 마크다운 렌더링 컴포넌트 매핑 — 분석가 톤의 답변용 단정한 스타일
const mdComponents = {
  h1: ({ children }) => (
    <h3 style={{
      fontSize: '15px', fontWeight: 600, color: '#f1f5f9',
      margin: '14px 0 8px', letterSpacing: '-0.01em',
      borderBottom: '1px solid #334155', paddingBottom: '6px',
    }}>{children}</h3>
  ),
  h2: ({ children }) => (
    <h3 style={{
      fontSize: '14px', fontWeight: 600, color: '#f1f5f9',
      margin: '12px 0 6px', letterSpacing: '-0.01em',
    }}>{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 style={{
      fontSize: '13px', fontWeight: 600, color: '#cbd5e1',
      margin: '10px 0 4px',
    }}>{children}</h4>
  ),
  p: ({ children }) => (
    <p style={{
      color: '#cbd5e1', fontSize: '13.5px', lineHeight: 1.7,
      margin: '6px 0',
    }}>{children}</p>
  ),
  strong: ({ children }) => (
    <strong style={{ color: '#f1f5f9', fontWeight: 600 }}>{children}</strong>
  ),
  em: ({ children }) => (
    <em style={{ color: '#94a3b8', fontStyle: 'normal' }}>{children}</em>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: '6px 0', paddingLeft: '18px', color: '#cbd5e1', fontSize: '13.5px', lineHeight: 1.7 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: '6px 0', paddingLeft: '20px', color: '#cbd5e1', fontSize: '13.5px', lineHeight: 1.7 }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li style={{ margin: '2px 0' }}>{children}</li>
  ),
  code: ({ inline, children }) => (
    inline
      ? <code style={{ background: '#0f172a', padding: '1px 6px', borderRadius: '3px', fontSize: '12px', color: '#60a5fa' }}>{children}</code>
      : <code style={{ display: 'block', background: '#0f172a', padding: '10px 12px', borderRadius: '4px', fontSize: '12px', color: '#cbd5e1', overflowX: 'auto' }}>{children}</code>
  ),
  blockquote: ({ children }) => (
    <blockquote style={{
      borderLeft: '3px solid #475569', margin: '8px 0', padding: '4px 12px',
      color: '#94a3b8', fontSize: '13px',
    }}>{children}</blockquote>
  ),
};

function AIAnalysis() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '안녕하세요! AI 분석 도우미입니다. 사출 설비 데이터, 생산 현황, 알람 분석 등에 대해 질문해주세요.',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingText, setStreamingText] = useState('');
  const [statusText, setStatusText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(() => {
    const saved = localStorage.getItem('ai-show-suggestions');
    return saved === null ? true : saved === 'true';
  });
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const toggleSuggestions = () => {
    setShowSuggestions(prev => {
      const next = !prev;
      localStorage.setItem('ai-show-suggestions', String(next));
      return next;
    });
  };

  // 메시지가 추가될 때마다 스크롤을 아래로
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // textarea 자동 높이 조절
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [inputValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputValue;
    setInputValue('');
    setIsLoading(true);
    setError(null);
    setStreamingText('');
    setStatusText('');

    try {
      // 멀티 에이전트 스트리밍 API 호출
      const conversationHistory = messages.filter(msg => msg.role === 'user' ||
        (msg.role === 'assistant' && !msg.content.includes('안녕하세요! AI 분석 도우미입니다')));

      await sendAgentMessageStream(
        messageToSend,
        conversationHistory,
        // onToken: 토큰이 올 때마다 호출
        (token, fullText) => {
          setStreamingText(fullText);
        },
        // onStatus: 상태 업데이트
        (status) => {
          setStatusText(status);
        },
        // onComplete: 완료 시 호출
        (response) => {
          const assistantMessage = {
            role: 'assistant',
            content: response.natural_language || '응답을 받지 못했습니다.',
            blocks: response.blocks || [],
            intent: response.intent,
            sqlQuery: response.sql_query,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
          setStreamingText('');
          setStatusText('');
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('AI 메시지 처리 실패:', error);

      const errorMessage = {
        role: 'assistant',
        content: '죄송합니다. 메시지를 처리하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      setError(error.message);
      setStreamingText('');
      setStatusText('');
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 부서별 추천 질문 가져오기
  const getSuggestedQuestions = () => {
    const user = getStoredUser();
    const department = user?.department || '';

    // 품질관리팀 추천 질문
    if (department.includes('품질') || department.includes('QC') || department.includes('QA')) {
      return [
        '최근 불량률 추이를 선그래프로 보여줘',
        '오늘 품질 알람이 가장 많이 발생한 설비는?',
        '사출 1호기의 최근 온도 변화에 대해서 분석해줘',
        '최근 7일간 설비별 불량 발생 현황을 막대그래프로 보여줘'
      ];
    }

    // 생산관리팀 추천 질문
    if (department.includes('생산') || department.includes('제조')) {
      return [
        '현재 가동 중인 설비 현황은?',
        '오늘 생산량이 가장 많은 설비는?',
        '최근 7일간 생산량 추이를 선그래프로 보여줘',
        '설비별 가동률을 비교해줘'
      ];
    }

    // 설비/유지보수팀 추천 질문
    if (department.includes('설비') || department.includes('유지보수') || department.includes('보전')) {
      return [
        '오늘 알람이 가장 많이 발생한 설비는?',
        '최근 7일간 설비별 알람 발생 추이를 보여줘',
        '사출 1호기의 최근 사이클 타임 변화를 분석해줘',
        '현재 비가동 설비 현황은?'
      ];
    }

    // 기본 추천 질문 (부서 정보 없거나 기타 부서)
    return [
      '현재 가동 중인 설비 현황은?',
      '오늘 알람이 가장 많이 발생한 설비는?',
      '최근 7일간 생산량 추이를 선그래프로 보여줘',
      '사출 1호기에 최근 샷 온도 변화에 대해서 분석해줘'
    ];
  };

  const suggestedQuestions = getSuggestedQuestions();

  const handleSuggestionClick = (question) => {
    setInputValue(question);
    textareaRef.current?.focus();
  };

  // ContentBlock 렌더링 함수 — 슬레이트 톤 단정한 디자인
  const renderContentBlock = (block, index) => {
    const { type, content } = block;

    switch (type) {
      case 'table':
        return <TableRenderer key={index} data={content} />;

      case 'chart':
        return (
          <div key={index} className="chart-container">
            <DynamicChart config={content} />
          </div>
        );

      case 'text':
        return (
          <div key={index} className="response-text">
            {content}
          </div>
        );

      case 'stat-cards': {
        const cards = content?.cards || [];
        return (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(cards.length, 4)}, minmax(0, 1fr))`,
              gap: '8px',
              margin: '14px 0',
            }}
          >
            {cards.map((card, i) => (
              <div
                key={i}
                style={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '14px 16px 14px 18px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {card.accent && (
                  <div style={{
                    position: 'absolute',
                    left: 0, top: 0, bottom: 0,
                    width: '3px',
                    background: card.accent,
                  }} />
                )}
                <div
                  style={{
                    fontSize: '11px',
                    color: '#94a3b8',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    marginBottom: '8px',
                  }}
                >
                  {card.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span
                    style={{
                      fontSize: '28px',
                      fontWeight: 600,
                      color: '#f1f5f9',
                      lineHeight: 1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {card.value}
                  </span>
                  {card.unit && (
                    <span style={{ fontSize: '13px', color: '#64748b' }}>{card.unit}</span>
                  )}
                </div>
                {card.delta && (
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>
                    {card.deltaPositive ? '↑' : '↓'} {card.delta}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      case 'recommendations': {
        const items = content?.items || [];
        const title = content?.title || '권장 조치';
        const priorityMeta = (p) => {
          switch (p) {
            case 'immediate':
              return { label: '즉시',    bg: '#7f1d1d', text: '#fecaca' };
            case 'today':
              return { label: '금일',    bg: '#3f3f46', text: '#e4e4e7' };
            case 'week':
              return { label: '이번 주', bg: '#334155', text: '#cbd5e1' };
            case 'ongoing':
            default:
              return { label: '지속',    bg: '#1e293b', text: '#94a3b8' };
          }
        };
        return (
          <div
            key={index}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              padding: '14px 18px',
              margin: '14px 0',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#94a3b8',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                marginBottom: '12px',
              }}
            >
              {title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {items.map((item, i) => {
                const pm = priorityMeta(item.priority);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: pm.bg,
                        color: pm.text,
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        padding: '3px 8px',
                        borderRadius: '3px',
                        minWidth: '48px',
                        textAlign: 'center',
                        marginTop: '1px',
                        flexShrink: 0,
                        border: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      {pm.label}
                    </span>
                    <span style={{ color: '#cbd5e1', fontSize: '13.5px', lineHeight: 1.6, flex: 1 }}>
                      {item.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case 'insights': {
        const items = content?.items || [];
        const title = content?.title || '핵심 인사이트';
        return (
          <div
            key={index}
            style={{
              background: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '6px',
              padding: '14px 18px',
              margin: '14px 0',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#94a3b8',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                marginBottom: '10px',
              }}
            >
              {title}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {items.map((item, i) => {
                const text = typeof item === 'string' ? item : item.text;
                const severity = typeof item === 'object' ? item.severity : null;
                const dotColor =
                  severity === 'critical' ? '#ef4444'
                  : severity === 'warning' ? '#f59e0b'
                  : '#64748b';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span
                      style={{
                        marginTop: '7px',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: dotColor,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: '#cbd5e1', fontSize: '13.5px', lineHeight: 1.65 }}>{text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      default:
        return (
          <div key={index} className="response-text">
            {JSON.stringify(content)}
          </div>
        );
    }
  };

  return (
    <MainLayout>
      <div className="ai-analysis-page">
        {/* 챗봇 본문 영역 */}
        <div className="ai-chat-container">
          <div className="ai-chat-wrapper">
            {/* 챗봇 헤더 */}
            <div className="ai-chat-header">
              <span className="material-symbols-outlined">smart_toy</span>
              <span className="ai-chat-title">AI 분석</span>
            </div>

            {/* 메시지 영역 */}
            <div className="ai-messages-container">
              {messages.map((message, index) => (
                <div key={index} className={`ai-message ${message.role}`}>
                  <div className="ai-message-avatar">
                    {message.role === 'assistant' ? (
                      <span className="material-symbols-outlined">smart_toy</span>
                    ) : (
                      <span className="material-symbols-outlined">person</span>
                    )}
                  </div>
                  <div className="ai-message-content">
                    <div className="ai-message-header">
                      <span className="ai-message-role">
                        {message.role === 'assistant' ? 'AI 어시스턴트' : '사용자'}
                      </span>
                      <span className="ai-message-time">{formatTime(message.timestamp)}</span>
                    </div>
                    <div className={`ai-message-text ${message.isError ? 'error' : ''}`}>
                      {/* 자연어 답변 — 마크다운 렌더링 */}
                      {message.content && (
                        <div className="response-text">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {message.content.replace(/<table[\s\S]*?<\/table>/gi, '').trim()}
                          </ReactMarkdown>
                        </div>
                      )}

                      {/* ContentBlock 렌더링 */}
                      {message.blocks && message.blocks.map((block, idx) => renderContentBlock(block, idx))}

                      {/* SQL 쿼리 (디버그용, 필요시 표시) */}
                      {message.sqlQuery && false && (  // false로 숨김 처리
                        <details className="sql-details">
                          <summary>SQL 쿼리 보기</summary>
                          <pre className="sql-query">
                            <code>{message.sqlQuery}</code>
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="ai-message assistant">
                  <div className="ai-message-avatar">
                    <span className="material-symbols-outlined">smart_toy</span>
                  </div>
                  <div className="ai-message-content">
                    <div className="ai-message-header">
                      <span className="ai-message-role">AI 어시스턴트</span>
                      {statusText && <span className="ai-status-text">{statusText}</span>}
                    </div>
                    {streamingText ? (
                      <div className="ai-message-text">
                        <div className="response-text streaming">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {streamingText}
                          </ReactMarkdown>
                          <span className="streaming-cursor">▌</span>
                        </div>
                      </div>
                    ) : (
                      <div className="ai-message-loading">
                        <span className="ai-loading-dot"></span>
                        <span className="ai-loading-dot"></span>
                        <span className="ai-loading-dot"></span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 추천 질문 - 로딩 중이 아닐 때 항상 표시. 우측 화살표로 접기/펼치기 */}
            {!isLoading && (
              <div className="ai-suggestions">
                <div
                  className="ai-suggestions-title"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: 'fit-content',
                  }}
                  onClick={toggleSuggestions}
                  title={showSuggestions ? '추천 질문 접기' : '추천 질문 펼치기'}
                >
                  <span>추천 질문</span>
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: '20px',
                      color: '#cbd5e1',
                      transform: showSuggestions ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.2s',
                    }}
                    aria-label={showSuggestions ? '추천 질문 접기' : '추천 질문 펼치기'}
                  >
                    keyboard_arrow_down
                  </span>
                </div>
                {showSuggestions && (
                  <div className="ai-suggestions-list">
                    {suggestedQuestions.map((question, index) => (
                      <button
                        key={index}
                        className="ai-suggestion-item"
                        onClick={() => handleSuggestionClick(question)}
                      >
                        <span className="material-symbols-outlined">lightbulb</span>
                        {question}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 입력 영역 */}
            <div className="ai-input-container">
              <form onSubmit={handleSubmit} className="ai-input-form">
                <textarea
                  ref={textareaRef}
                  className="ai-input-textarea"
                  placeholder="질문을 입력하세요... (Shift+Enter로 줄바꿈)"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="ai-input-submit"
                  disabled={!inputValue.trim() || isLoading}
                >
                  <span className="material-symbols-outlined">send</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default AIAnalysis;
